import { ReactNode, useState } from 'react';
import { useShiftsAssignment, useShiftsListStore } from '@libs/store/shifts';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { useNotification } from '@libs/snackbar';
import { isServerError } from '@libs/utils/error';
import { parseError } from '@libs/api/errors';
import { AssignDialog, ConfirmCreateDoubleShiftDialog, UnassignDialog } from '@pages/schedule/dialogs';
import { AssigneeButton } from './assigneeButton';
import { AssigneeOld, ShiftOld } from '@libs/models/shifts/shift';
import { alpha, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

type ShiftAssigneeButtonProps = {
  shift: ShiftOld & {
    assignees: AssigneeOld[];
  };
  disabled?: boolean;
  fullWidth?: boolean;
  onAssigned?: (shiftId: string, userId: string) => void;
  onUnassigned?: (shiftId: string, reason: string, reasonComment: string) => void;
  unassignWarningMessage?: ReactNode;
  allowOnlyInternals?: boolean;
};
export function ShiftAssigneeButton({
  shift,
  fullWidth,
  onAssigned,
  onUnassigned,
  disabled,
  unassignWarningMessage,
  allowOnlyInternals,
}: ShiftAssigneeButtonProps) {
  const { assignShift, unassignShift, loadingById, checkAssignOverlap } = useShiftsAssignment();
  const { setCurrentShift } = useShiftsListStore();
  const { callRefresh } = useRefreshSyncStore();
  const { showSuccessIntl, showError, showErrorIntl } = useNotification();
  const { requestAssignShiftOnExternal } = useShiftsAssignment();

  const [opedDialog, setOpedDialog] = useState(false);
  const [opedAssignDialog, setOpedAssignDialog] = useState(false);
  const [sendingData, setSendingData] = useState<{ shiftId: string; resourceId: string } | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [doubleShiftId, setDoubleShiftId] = useState<string | null>(null);

  const shiftId = shift.id;
  const loading = loadingById[shiftId] ?? false;
  const handleAssign = async (userId: string, staffingType?: string) => {
    if (loading) return;
    if (staffingType === 'Agency') {
      await requestAssignShiftOnExternal(shift.id, userId);
      callRefresh();
      setOpedAssignDialog(false);
      onAssigned?.(shiftId, userId);
      return;
    }
    if (shift.recurrence && shift.recurrence === 'single') {
      const overlapInfo = await checkAssignOverlap({
        shiftID: shift.id,
        assigneeId: userId,
      });
      if (overlapInfo.canCreateDoubleShift) {
        setDoubleShiftId(overlapInfo.overlappedShiftId);
        setOpenConfirmDialog(true);
        setSendingData({ shiftId: shift.id as string, resourceId: userId });
        return;
      }
    }
    performAssign(shiftId, userId);
  };

  const performAssign = async (shiftId: string, resourceId: string, doubleShiftId?: string | null) => {
    await assignShift(shiftId, resourceId, doubleShiftId)
      .then(() => {
        callRefresh();
        setCurrentShift(shift.id);
        showSuccessIntl('shifts.assignSuccessful');
        setOpedAssignDialog(false);
        onAssigned?.(shiftId, resourceId);
      })
      .catch((error: unknown) => {
        if (isServerError(error) && !error.response?.data.errors) {
          showErrorIntl(`shifts.assignError.${error.response?.data.title}`);
          return;
        }
        showError(parseError(error).message);
      });
  };

  const handleUnassign = async (reason: string, reasonComment: string) => {
    if (loading) return;
    await unassignShift(shiftId, { reason, reasonComment })
      .then(() => {
        callRefresh();
        setCurrentShift(shift.id);
        setOpedDialog(false);
        onUnassigned?.(shiftId, reason, reasonComment);
        showSuccessIntl('shifts.unassignSuccessful');
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  const badgeStatus = shift.assignRequest?.status;
  const badgeReason = shift.assignRequest?.declineReason;

  const externalShiftAssigned = shift.assignees.some((a) => a.agencyName);
  const isInternalShiftAssigned = shift.assignees.some((a) => !a.agencyName);
  const isUnassigned = shift.assignees.length === 0 && !shift.assignRequest;
  const isAgencyShiftApproved = shift.assignRequest?.status === 'Approved';
  const isAgencyShiftPending = shift.assignRequest?.status === 'Pending';
  const siAgencyDirectlyAssigned = externalShiftAssigned && !shift.assignRequest;
  const isReassignedShift =
    shift.assignees.length &&
    shift.assignees.find((assignee) => shift.assignRequest?.staff.id === assignee.id) === undefined &&
    shift.assignRequest?.status === 'Declined';

  const badgeTooltip =
    badgeStatus === 'Pending'
      ? 'Shift assignment is pending approval.'
      : badgeStatus === 'Declined'
        ? `Shift assignment for ${shift.assignRequest?.staff.firstName} ${shift.assignRequest?.staff.lastName} was declined. Reason: ${badgeReason}`
        : badgeStatus === 'Approved' || externalShiftAssigned
          ? 'Shift assignment approved.'
          : '';

  return (
    <>
      <Tooltip title={badgeTooltip} placement="left">
        <Stack direction="row" alignItems={'center'} sx={{ width: '100%', height: '100%' }}>
          <Box
            sx={(theme) => ({
              cursor: 'default',
              minWidth: '4px',
              height: '80%',
              // borderTopLeftRadius: theme.spacing(0.5),
              // borderBottomLeftRadius: theme.spacing(0.5),
              borderRadius: theme.spacing(1),
              backgroundColor:
                isInternalShiftAssigned || isUnassigned
                  ? 'transparent'
                  : isReassignedShift || isAgencyShiftApproved || siAgencyDirectlyAssigned
                    ? theme.palette.success.light
                    : isAgencyShiftPending
                      ? theme.palette.warning.light
                      : theme.palette.error.dark,
            })}
          ></Box>

          <AssigneeButton
            value={shift.assignees}
            disabled={shift.assignRequest?.status === 'Pending' || disabled}
            fullWidth={fullWidth}
            loading={loading}
            onAssignClick={() => setOpedAssignDialog(true)}
            onUnassignClick={() => setOpedDialog(true)}
          />
        </Stack>
      </Tooltip>
      <AssignDialog
        open={opedAssignDialog}
        shift={shift}
        onClose={() => setOpedAssignDialog(false)}
        onSubmit={handleAssign}
        allowOnlyInternals={allowOnlyInternals}
        loading={loading}
      />
      <UnassignDialog
        open={opedDialog}
        shift={{
          ...shift,
          assigneeName: shift.assignees.map((a) => a.name).join(', '),
        }}
        onClose={() => setOpedDialog(false)}
        onSubmit={handleUnassign}
        loading={loading}
        warningMessage={unassignWarningMessage}
      />
      <ConfirmCreateDoubleShiftDialog
        open={openConfirmDialog}
        onClose={() => {
          setDoubleShiftId(null);
          setOpenConfirmDialog(false);
        }}
        onSubmit={async () => {
          if (!sendingData) return;
          await performAssign(sendingData.shiftId as string, sendingData.resourceId as string, doubleShiftId);
        }}
      />
    </>
  );
}
